// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Button, Layout, Result } from '@nackle/paper';
import NackleMenu from '@nackle/menu';
const { Content } = Layout;
import { GOLD_COMPANY_ID } from './constants.js';

// Context
import { NavigationContextProvider } from './context/navigation-context.jsx';

// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';
import { routes, bundleRoutingMiddleware } from './app-routing.jsx';
import { UnsavedChangesModal } from '@nackle/studio-tools';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth from './app-auth.js';
import { applyRequestMiddleware, authRouteMiddleware, authRequestMiddleware } from '@nackle/studio-tools';

// Components
import { AppHeader, AppFooter, AppError } from '@nackle/studio-tools';

// Services
import CMXService from './services/cmx';

// Styles
import './styles/app.less';

/*******************************************************************************
 *  1. App (root level component)
 */
class App extends React.Component {
    state = {
        loaded: false,
        cmxBundleLoaded: false,
    };

    handleCompanyChange = async company => {
        if ( company.id === GOLD_COMPANY_ID ) {
            routeHistory.push( `/${ company.id }/menu-types` );
        } else {
            routeHistory.push( `/${ company.id }/menu-types/configuration` );
        }

        
        await auth.delegate( company.id );

        const cmxService = new CMXService( auth.state );
        await cmxService.loadBundles( [ 'navigation', 'studio-in-context-help' ], undefined, company.id );
        
        this.setState( {
            ...this.state,
            user: auth.state
        } );
    };

    
    render() {
        const { loaded, cmxBundleLoaded, user } = this.state;
        return (
            <AppError>
                <Route render={ ( route ) => {
                    // set the title
                    document.title = route && route.match ? route.match.title : 'Home';

                    const companyId = route.state?.params?.companyId;
                    return (
                        <Bridge
                            auth={ auth }
                            onSignIn={ ( user ) => this.handleSignIn( user, companyId ) }
                        >
                            {
                                loaded ? (
                                    <Layout className="root-layout">
                                        <AppHeader 
                                            auth={ auth } 
                                            companyId={ companyId } 
                                            onCompanyChange={ this.handleCompanyChange } 
                                        />
                                        <Layout>
                                            <NackleMenu
                                                auth={ auth }
                                                cmxBundleLoaded={ cmxBundleLoaded }
                                                collapsible={ true }
                                            />
                                            <Layout className="page-layout">
                                                <Content className="app-content">
                                                    {
                                                        route.match ?
                                                            <div key={ route.state.params.companyId } className="app-container">
                                                                <div className="main-container">
                                                                    { React.cloneElement( route.component, { companyId, user } ) }
                                                                </div>
                                                            </div>
                                                        : (
                                                            <Result
                                                                status="404"
                                                                title="404"
                                                                subTitle="Sorry, the page you visited does not exist."
                                                                extra={ <Button type='primary' href='/'>Back Home</Button> }
                                                            />
                                                        )
                                                    }
                                                </Content>
                                                <AppFooter />
                                            </Layout>
                                        </Layout>
                                    </Layout>
                                ) : null
                            }
                        </Bridge>
                    );
                } } />
            </AppError>
        );
    }

    handleSignIn = async ( user, companyId ) => {
        await routeHistory.refresh();
        const cmxService = new CMXService( auth.state );
        await cmxService.loadBundles( [ 'navigation', 'studio-in-context-help' ], undefined, companyId );

        const match = window.location.pathname.match(
            /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
        );
        if ( match?.[ 0 ] ) {
            await auth.delegate( match[ 0 ] );
            user = auth.state;
        } else {
            await auth.delegate( GOLD_COMPANY_ID );
        }

        this.setState( {
            loaded: true,
            cmxBundleLoaded: true,
            user,
            locale: user.claims.locale
        } );
    };
}

/*******************************************************************************
 *  2. Connect Routing
 */
routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [ authRouteMiddleware( auth ), bundleRoutingMiddleware, UnsavedChangesModal.routerMiddleware ] );

/*******************************************************************************
 *  3. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware( auth ) );

/*******************************************************************************
 * 4. Render the the app
 */

const container = document.getElementById( 'app-root' );
const root = createRoot( container );
root.render(
    <NavigationContextProvider>
        <App />,
    </NavigationContextProvider>
);